import { AuthModal, TextField, useAuth } from '@kitopi-com/customer-auth';
import { sites } from 'context/sites';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { useFetchSites } from 'hooks/useFetchSites';
import { logos } from 'hooks/utils/logos';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isDevMode } from 'utils/constants';
import { getLogo } from 'utils/getLogo';
import defaultLogo from '../../assets/images/kitopi.webp';

import {
  HeaderContainer,
  LogoCell,
  RowNumberCell,
  SiteDescription,
  SiteLogo,
  SiteName,
  SitesTable,
  SitesTestContainer,
  SitesTestTitle,
  SitesTestTitleInline,
  StatusBadge,
  TabBadge,
  TabButton,
  TabContainer,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TriggerWrapper,
} from './SitesDashboard.styles';

interface SiteWithLogo {
  name: string;
  desc: string;
  hasLogo: boolean;
  logo: string;
  isLaunched: boolean;
  deviceCount: number;
}

type FilterType = 'all' | 'launched' | 'not-launched' | 'with-logo';

export const SitesDashboard = () => {
  const { t } = useTranslation('wifi');
  const { data: launchedSites, isLoading, error } = useFetchSites();
  const [sitesWithLogos, setSitesWithLogos] = useState<SiteWithLogo[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const {
    state: { isSignedIn, checkIsSignedIn: checkIsSignedInAsKitopiCustomer },
  } = useAuth();

  useEffectOnce(async () => {
    await checkIsSignedInAsKitopiCustomer();
  });

  useEffect(() => {
    const loadSitesWithLogos = async () => {
      if (!launchedSites) return;

      const sitesDataPromises = sites.map(async site => {
        const hasLogo = !!logos[site.name];
        const logoFromName = await getLogo(site.name);
        const logo = logoFromName || defaultLogo;

        const launchedSite = launchedSites.find(ls => ls.name === site.name);
        const isLaunched = !!launchedSite;
        const deviceCount = launchedSite?.device_count || 0;

        return {
          name: site.name,
          desc: site.desc,
          hasLogo,
          logo,
          isLaunched,
          deviceCount,
        };
      });

      const sitesData = await Promise.all(sitesDataPromises);
      setSitesWithLogos(sitesData);
    };

    loadSitesWithLogos();
  }, [launchedSites]);

  const filteredSites = sitesWithLogos.filter(site => {
    const matchesSearch =
      site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.desc.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Filter by selected option
    switch (activeFilter) {
      case 'all':
        return true;
      case 'launched':
        return site.isLaunched;
      case 'not-launched':
        return !site.isLaunched;
      case 'with-logo':
        return site.hasLogo;
      default:
        return true;
    }
  });

  const stats = {
    total: sitesWithLogos.length,
    launched: sitesWithLogos.filter(site => site.isLaunched).length,
    notLaunched: sitesWithLogos.filter(site => !site.isLaunched).length,
    withLogo: sitesWithLogos.filter(site => site.hasLogo).length,
  };

  if (!isDevMode) {
    return null;
  }

  if (!isSignedIn) {
    return (
      <SitesTestContainer>
        <SitesTestTitle>{t('sitesDashboardTitle')}</SitesTestTitle>
        <TriggerWrapper>
          <AuthModal authTriggerTitle={t('sitesDashboardLoginRequired')} />
        </TriggerWrapper>
      </SitesTestContainer>
    );
  }

  if (isLoading) {
    return (
      <SitesTestContainer>
        <SitesTestTitle>{t('sitesDashboardTitle')}</SitesTestTitle>
        <div style={{ textAlign: 'center', marginTop: '2rem', color: '#666' }}>
          Loading sites data...
        </div>
      </SitesTestContainer>
    );
  }

  if (error) {
    return (
      <SitesTestContainer>
        <SitesTestTitle>{t('sitesDashboardTitle')}</SitesTestTitle>
        <div style={{ textAlign: 'center', marginTop: '2rem', color: '#ff0000' }}>
          Error loading sites data: {error.message}
        </div>
      </SitesTestContainer>
    );
  }

  return (
    <SitesTestContainer>
      <HeaderContainer>
        <SitesTestTitleInline>{t('sitesDashboardTitle')}</SitesTestTitleInline>
        <TextField
          placeholder={t('sitesDashboardSearchPlaceholder')}
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          width="250px"
        />
      </HeaderContainer>

      <TabContainer>
        <TabButton isActive={activeFilter === 'all'} onClick={() => setActiveFilter('all')}>
          {t('sitesDashboardFilterAll')}
          <TabBadge>{stats.total}</TabBadge>
        </TabButton>
        <TabButton
          isActive={activeFilter === 'launched'}
          onClick={() => setActiveFilter('launched')}
        >
          {t('sitesDashboardFilterLaunched')}
          <TabBadge>{stats.launched}</TabBadge>
        </TabButton>
        <TabButton
          isActive={activeFilter === 'not-launched'}
          onClick={() => setActiveFilter('not-launched')}
        >
          {t('sitesDashboardFilterNotLaunched')}
          <TabBadge>{stats.notLaunched}</TabBadge>
        </TabButton>
        <TabButton
          isActive={activeFilter === 'with-logo'}
          onClick={() => setActiveFilter('with-logo')}
        >
          {t('sitesDashboardFilterWithLogo')}
          <TabBadge>{stats.withLogo}</TabBadge>
        </TabButton>
      </TabContainer>

      <SitesTable>
        <TableHeader>
          <tr>
            <TableHeaderCell>#</TableHeaderCell>
            <TableHeaderCell>{t('sitesDashboardTableLogo')}</TableHeaderCell>
            <TableHeaderCell>{t('sitesDashboardTableCode')}</TableHeaderCell>
            <TableHeaderCell>{t('sitesDashboardTableDescription')}</TableHeaderCell>
            <TableHeaderCell>{t('sitesDashboardTableAccessPoints')}</TableHeaderCell>
            <TableHeaderCell>{t('sitesDashboardTableStatus')}</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {filteredSites.map((site, index) => (
            <TableRow key={site.name}>
              <RowNumberCell>{index + 1}</RowNumberCell>
              <LogoCell>
                <SiteLogo>
                  <img
                    src={site.logo}
                    alt={`${site.desc} logo`}
                    onError={e => {
                      const target = e.target as HTMLImageElement;
                      target.src = defaultLogo;
                    }}
                  />
                </SiteLogo>
              </LogoCell>
              <TableCell>
                <SiteName>{site.name}</SiteName>
              </TableCell>
              <TableCell>
                <SiteDescription>{site.desc}</SiteDescription>
              </TableCell>
              <TableCell>{site.isLaunched ? site.deviceCount : '-'}</TableCell>
              <TableCell>
                <StatusBadge hasLogo={site.isLaunched}>
                  {site.isLaunched ? t('sitesDashboardLaunched') : t('sitesDashboardNotLaunched')}
                </StatusBadge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SitesTable>

      {filteredSites.length === 0 && (
        <div style={{ textAlign: 'center', marginTop: '2rem', color: '#666' }}>
          {t('sitesDashboardNoResults')}
        </div>
      )}
    </SitesTestContainer>
  );
};
